/**
 * 19:29 VAULT — Custom Wishlist
 * Stores product handles in localStorage.
 * Cross-device sync when SYNC_ENDPOINT is set + customer is logged in.
 * Works across product pages, product cards, and the /pages/wishlist page.
 */

const VAULT_WISHLIST_KEY = 'vault-wishlist-v1';

/* Cross-device sync endpoint (Cloudflare Worker). Set to `null` to disable
   remote sync (localStorage only). When set, logged-in customers get their
   wishlist merged across devices. See /wishlist-sync-worker.js for deploy. */
const SYNC_ENDPOINT = 'https://vault-wishlist-sync.tanishqdavar.workers.dev';

const VaultWishlist = {
  get() {
    try {
      return JSON.parse(localStorage.getItem(VAULT_WISHLIST_KEY) || '[]');
    } catch (_) {
      return [];
    }
  },

  save(list, opts) {
    // Wrap setItem — Facebook / Instagram in-app browsers in "private mode"
    // (they open pages in a stateless WebView) throw QuotaExceededError or
    // SecurityError on any localStorage write. Without this guard, the
    // wishlist toggle silently crashes the JS thread for ~30% of our traffic
    // (FacebookApp WebView per Clarity's browser breakdown). Still dispatches
    // the event so in-memory state stays consistent for the current session.
    try {
      localStorage.setItem(VAULT_WISHLIST_KEY, JSON.stringify(list));
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('vault:wishlist:update', {
      detail: { list, skipRemote: !!(opts && opts.skipRemote) }
    }));
  },

  has(handle) {
    return this.get().includes(handle);
  },

  add(handle) {
    const list = this.get();
    if (!list.includes(handle)) {
      list.push(handle);
      this.save(list);
    }
  },

  remove(handle) {
    const list = this.get().filter(h => h !== handle);
    this.save(list);
  },

  toggle(handle) {
    this.has(handle) ? this.remove(handle) : this.add(handle);
    return this.has(handle);
  },

  count() {
    return this.get().length;
  },

  /* Sync helpers — no-op unless SYNC_ENDPOINT is configured and customer
     is signed in. Handles are the source of truth; on pull, we union the
     local and remote lists so nothing is lost when a customer signs in on
     a new device with pre-existing local picks. */
  _canSync() {
    return !!(SYNC_ENDPOINT && window.VaultCustomer && window.VaultCustomer.loggedIn && window.VaultCustomer.id);
  },

  _remoteKey() {
    return String(window.VaultCustomer.id);
  },

  async pullRemote() {
    if (!this._canSync()) return;
    try {
      const res = await fetch(`${SYNC_ENDPOINT}?customer_id=${encodeURIComponent(this._remoteKey())}`, {
        method: 'GET',
        credentials: 'omit',
      });
      if (!res.ok) return;
      const remote = await res.json();
      if (!Array.isArray(remote)) return;
      const local = this.get();
      const merged = Array.from(new Set([...local, ...remote]));
      if (merged.length !== local.length || merged.some((h, i) => h !== local[i])) {
        this.save(merged, { skipRemote: true });
        if (merged.length !== remote.length) this.pushRemote();
      }
    } catch (_) {}
  },

  async pushRemote() {
    if (!this._canSync()) return;
    try {
      await fetch(`${SYNC_ENDPOINT}?customer_id=${encodeURIComponent(this._remoteKey())}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ handles: this.get() }),
      });
    } catch (_) {}
  }
};

/* ─── Wishlist button toggle ──────────────────────────────────────────────── */

function initWishlistButtons() {
  document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
    const handle = btn.dataset.wishlistBtn;
    if (!handle) return;
    updateButtonState(btn, handle);

    btn.addEventListener('click', function (e) {
      e.preventDefault();

      // Guests toggle locally (localStorage). If they later sign in,
      // pullRemote() unions their local picks with the server list, so
      // nothing is lost. The wishlist page then prompts guests to sign
      // in / create an account to save their wishlist across devices.
      VaultWishlist.toggle(handle);
      updateButtonState(btn, handle);

      btn.classList.add('vault-wishlist-btn--pulse');
      btn.addEventListener('animationend', () => btn.classList.remove('vault-wishlist-btn--pulse'), { once: true });
    });
  });
}

function updateButtonState(btn, handle) {
  const active = VaultWishlist.has(handle);
  btn.classList.toggle('vault-wishlist-btn--active', active);
  btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist');
  btn.setAttribute('aria-pressed', active ? 'true' : 'false');
}

/* ─── Header count badge ──────────────────────────────────────────────────── */

function updateWishlistCount() {
  document.querySelectorAll('[data-wishlist-count]').forEach(el => {
    const count = VaultWishlist.count();
    el.textContent = count;
    el.style.display = count > 0 ? '' : 'none';
  });
}

/* ─── Wishlist page renderer ──────────────────────────────────────────────── */

async function renderWishlistPage() {
  const container = document.getElementById('vault-wishlist-page');
  if (!container) return;

  const handles = VaultWishlist.get();
  const emptyEl = document.getElementById('vault-wishlist-empty');
  const gridEl  = document.getElementById('vault-wishlist-grid');

  if (!handles.length) {
    if (emptyEl) emptyEl.style.display = '';
    if (gridEl)  gridEl.style.display  = 'none';
    return;
  }

  if (emptyEl) emptyEl.style.display = 'none';
  if (gridEl)  gridEl.style.display  = 'none';

  const products = await Promise.all(
    handles.map(h =>
      fetch(`/products/${h}.js`)
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    )
  );

  const valid = products.filter(Boolean);
  if (!valid.length) {
    if (emptyEl) emptyEl.style.display = '';
    return;
  }

  gridEl.innerHTML = '';
  valid.forEach(product => {
    gridEl.appendChild(buildProductCard(product));
  });
  gridEl.style.display = '';

  // Initialise wishlist buttons on newly created cards
  initWishlistButtons();

  // Sync count badges now that the page is rendered (without re-triggering renderWishlistPage)
  updateWishlistCount();
  updateCartWishlistRow();
}

function buildProductCard(product) {
  const variant   = product.variants[0];
  const image     = product.featured_image || '';
  const price     = formatMoney(variant.price);
  const available = product.available;

  const card = document.createElement('div');
  card.className = 'vault-wishlist-card';
  card.innerHTML = `
    <a href="/products/${product.handle}" class="vault-wishlist-card__media">
      ${image
        ? `<img src="${image}" alt="${escapeHTML(product.title)}" loading="lazy">`
        : '<div class="vault-wishlist-card__placeholder"></div>'
      }
    </a>
    <div class="vault-wishlist-card__info">
      <a href="/products/${product.handle}" class="vault-wishlist-card__title">${escapeHTML(product.title)}</a>
      <p class="vault-wishlist-card__price">${price}</p>
      <div class="vault-wishlist-card__actions">
        <a href="/products/${product.handle}" class="vault-wishlist-card__atc ${!available ? 'vault-wishlist-card__atc--soldout' : ''}">
          ${available ? 'Add to bag' : 'Sold out'}
        </a>
        <button
          class="vault-wishlist-btn vault-wishlist-btn--active vault-wishlist-btn--remove"
          data-wishlist-btn="${product.handle}"
          aria-label="Remove from wishlist"
          aria-pressed="true"
        >
          ${bookmarkSVG()}
        </button>
      </div>
    </div>
  `;
  return card;
}

function bookmarkSVG(size = 16) {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
}

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const amountNoDecimals = Math.round(cents / 100).toString();
  const fmt = window.Shopify && window.Shopify.moneyFormat;
  if (!fmt) return amount;
  return fmt
    .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
    .replace('{{amount_with_comma_separator}}', amount)
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount}}', amount);
}

function escapeHTML(str) {
  return String(str).replace(/[&<>"']/g, s => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[s]));
}

/* ─── Bootstrap ───────────────────────────────────────────────────────────── */

function updateCartWishlistRow() {
  const links  = document.querySelectorAll('.vault-cart-wishlist-row');
  const labels = document.querySelectorAll('.vault-cart-wishlist-label');
  if (!links.length) return;

  const loggedIn = window.VaultCustomer && window.VaultCustomer.loggedIn;
  const count = VaultWishlist.count();

  links.forEach(link => {
    if (loggedIn) {
      link.href = '/pages/wishlist';
    } else {
      link.href = '/account/login?return_url=%2Fpages%2Fwishlist';
    }
  });

  labels.forEach(label => {
    label.textContent = loggedIn && count > 0 ? `Wishlist (${count})` : 'Wishlist';
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initWishlistButtons();
  updateWishlistCount();
  updateCartWishlistRow();
  renderWishlistPage();
  // Pull remote wishlist on load if signed in — union with local
  VaultWishlist.pullRemote();
});

// Re-sync whenever wishlist changes
document.addEventListener('vault:wishlist:update', (e) => {
  updateWishlistCount();
  updateCartWishlistRow();
  if (document.getElementById('vault-wishlist-page')) renderWishlistPage();
  // Push to remote on user-initiated changes only (not on merges from pullRemote)
  if (!(e.detail && e.detail.skipRemote)) VaultWishlist.pushRemote();
});

window.VaultWishlist = VaultWishlist;
